// Google Identity Services (GIS) helper — admin "Sign in with Google".
//
// We use the ID-token flow: GIS hands us a Google ID token (credential), which
// we POST to the backend (/api/v2/admin/auth/google). The backend verifies it
// (audience + hosted-domain + admin allowlist) and returns our own app JWT.
// GIS only proves identity; authorization stays server-side.

const GIS_SRC = 'https://accounts.google.com/gsi/client'
const WORKSPACE_DOMAIN = 'soundrepublica.com'

let scriptPromise = null
let renewTimer = null

/** Load the GIS client script once; resolves with window.google. */
export function loadGis() {
    if (scriptPromise) return scriptPromise
    scriptPromise = new Promise((resolve, reject) => {
        if (window.google && window.google.accounts && window.google.accounts.id) {
            return resolve(window.google)
        }
        const s = document.createElement('script')
        s.src = GIS_SRC
        s.async = true
        s.defer = true
        s.onload = () => resolve(window.google)
        s.onerror = () => reject(new Error('Failed to load Google Identity Services'))
        document.head.appendChild(s)
    })
    return scriptPromise
}

/**
 * Initialize GIS with our client id + credential callback.
 * `hd` is a domain hint only — the real domain gate is server-side.
 */
export async function initGoogleSignIn({ clientId, onCredential, autoSelect = false }) {
    const google = await loadGis()
    google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => onCredential(response.credential),
        auto_select: autoSelect,
        cancel_on_tap_outside: false,
        hd: WORKSPACE_DOMAIN,
        ux_mode: 'popup',
    })
    return google
}

/** Render the official "Sign in with Google" button into `el`. */
export async function renderGoogleButton(el, options = {}) {
    const google = await loadGis()
    google.accounts.id.renderButton(el, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        text: 'signin_with',
        width: 320,
        ...options,
    })
}

/** Decode a JWT's `exp` (seconds) without verifying. Returns null on failure. */
export function decodeJwtExp(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        return typeof payload.exp === 'number' ? payload.exp : null
    } catch {
        return null
    }
}

/**
 * Best-effort silent re-auth: shortly before the app JWT expires, re-prompt
 * GIS with auto-select so a fresh credential can be exchanged without a click.
 * If GIS can't do it silently, the axios 401 handler still falls back to /login.
 */
export async function scheduleSilentRenew({ clientId, token, onCredential, leadSeconds = 120 }) {
    cancelSilentRenew()
    const exp = decodeJwtExp(token)
    if (!exp) return
    const delayMs = (exp - leadSeconds) * 1000 - Date.now()
    if (delayMs <= 0) return
    renewTimer = setTimeout(async () => {
        try {
            await initGoogleSignIn({ clientId, onCredential, autoSelect: true })
            const google = await loadGis()
            google.accounts.id.prompt()
        } catch {
            // Silent failure is fine — expiry → 401 → /login is the fallback.
        }
    }, delayMs)
}

export function cancelSilentRenew() {
    if (renewTimer) {
        clearTimeout(renewTimer)
        renewTimer = null
    }
}
